import {
  ITaskProjectTask,
  TaskProjectStatus,
  TaskProjectView,
} from '@common/interfaces/taskProject'
import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { type IPlacementMove } from '../../helpers/taskPlacement'
import { createAction } from '../types'

export const prefix = 'taskProjects'

export const [LIST_PROJECTS_REQUEST, LIST_PROJECTS_SUCCESS, LIST_PROJECTS_FAILURE] =
  createRequestTypes(prefix, 'LIST_PROJECTS')

export const listProjects = createAction((params?: object) => ({
  type: [LIST_PROJECTS_REQUEST, LIST_PROJECTS_SUCCESS, LIST_PROJECTS_FAILURE],
  promise: (api: IApi) => api.taskProjects.list(params),
}))

export const [CREATE_PROJECT_REQUEST, CREATE_PROJECT_SUCCESS, CREATE_PROJECT_FAILURE] =
  createRequestTypes(prefix, 'CREATE_PROJECT')

export const createProject = createAction((data: object) => ({
  type: [CREATE_PROJECT_REQUEST, CREATE_PROJECT_SUCCESS, CREATE_PROJECT_FAILURE],
  promise: (api: IApi) => api.taskProjects.create(data),
}))

export const [SHOW_PROJECT_REQUEST, SHOW_PROJECT_SUCCESS, SHOW_PROJECT_FAILURE] =
  createRequestTypes(prefix, 'SHOW_PROJECT')

export const showProject = createAction((id: string) => ({
  type: [SHOW_PROJECT_REQUEST, SHOW_PROJECT_SUCCESS, SHOW_PROJECT_FAILURE],
  promise: (api: IApi) => api.taskProjects.show(id),
}))

export const [
  LIST_FAVORITE_PROJECTS_REQUEST,
  LIST_FAVORITE_PROJECTS_SUCCESS,
  LIST_FAVORITE_PROJECTS_FAILURE,
] = createRequestTypes(prefix, 'LIST_FAVORITE_PROJECTS')

export const listFavoriteProjects = createAction(() => ({
  type: [
    LIST_FAVORITE_PROJECTS_REQUEST,
    LIST_FAVORITE_PROJECTS_SUCCESS,
    LIST_FAVORITE_PROJECTS_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.listFavorites(),
}))

export const [
  SET_PROJECT_FAVORITE_REQUEST,
  SET_PROJECT_FAVORITE_SUCCESS,
  SET_PROJECT_FAVORITE_FAILURE,
] = createRequestTypes(prefix, 'SET_PROJECT_FAVORITE')

export const setProjectFavorite = createAction((id: string, isFavorite: boolean) => ({
  type: [SET_PROJECT_FAVORITE_REQUEST, SET_PROJECT_FAVORITE_SUCCESS, SET_PROJECT_FAVORITE_FAILURE],
  promise: (api: IApi) => api.taskProjects.setFavorite(id, { isFavorite }),
}))

export const [
  SET_PROJECT_MY_TASKS_VISIBILITY_REQUEST,
  SET_PROJECT_MY_TASKS_VISIBILITY_SUCCESS,
  SET_PROJECT_MY_TASKS_VISIBILITY_FAILURE,
] = createRequestTypes(prefix, 'SET_PROJECT_MY_TASKS_VISIBILITY')

export const setProjectMyTasksVisibility = createAction((id: string, showInMyTasks: boolean) => ({
  type: [
    SET_PROJECT_MY_TASKS_VISIBILITY_REQUEST,
    SET_PROJECT_MY_TASKS_VISIBILITY_SUCCESS,
    SET_PROJECT_MY_TASKS_VISIBILITY_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.setMyTasksVisibility(id, { showInMyTasks }),
}))

export const [SET_PROJECT_STATUS_REQUEST, SET_PROJECT_STATUS_SUCCESS, SET_PROJECT_STATUS_FAILURE] =
  createRequestTypes(prefix, 'SET_PROJECT_STATUS')

export const setProjectStatus = createAction((id: string, status: TaskProjectStatus) => ({
  type: [SET_PROJECT_STATUS_REQUEST, SET_PROJECT_STATUS_SUCCESS, SET_PROJECT_STATUS_FAILURE],
  promise: (api: IApi) => api.taskProjects.setStatus(id, { status }),
}))

export const [UPDATE_PROJECT_REQUEST, UPDATE_PROJECT_SUCCESS, UPDATE_PROJECT_FAILURE] =
  createRequestTypes(prefix, 'UPDATE_PROJECT')

export const updateProject = createAction((id: string, data: object) => ({
  type: [UPDATE_PROJECT_REQUEST, UPDATE_PROJECT_SUCCESS, UPDATE_PROJECT_FAILURE],
  promise: (api: IApi) => api.taskProjects.update(id, data),
}))

export const [DELETE_PROJECT_REQUEST, DELETE_PROJECT_SUCCESS, DELETE_PROJECT_FAILURE] =
  createRequestTypes(prefix, 'DELETE_PROJECT')

export const deleteProject = createAction((id: string) => ({
  type: [DELETE_PROJECT_REQUEST, DELETE_PROJECT_SUCCESS, DELETE_PROJECT_FAILURE],
  promise: (api: IApi) => api.taskProjects.delete(id),
  // DeleteProject's response is just a success message; the favorites reducer
  // needs the id to drop the deleted project's tab.
  params: { id },
}))

export const [LIST_PROJECT_TASKS_REQUEST, LIST_PROJECT_TASKS_SUCCESS, LIST_PROJECT_TASKS_FAILURE] =
  createRequestTypes(prefix, 'LIST_PROJECT_TASKS')

export const listProjectTasks = createAction((id: string, params: object = {}) => ({
  type: [LIST_PROJECT_TASKS_REQUEST, LIST_PROJECT_TASKS_SUCCESS, LIST_PROJECT_TASKS_FAILURE],
  promise: (api: IApi) => api.taskProjects.listTasks(id, params),
  // The project rides along so a reducer can tell one board's pages from another's.
  params: { ...params, projectId: id },
}))

export const [
  GET_PROJECT_STATUS_SUMMARY_REQUEST,
  GET_PROJECT_STATUS_SUMMARY_SUCCESS,
  GET_PROJECT_STATUS_SUMMARY_FAILURE,
] = createRequestTypes(prefix, 'GET_PROJECT_STATUS_SUMMARY')

export const getProjectStatusSummary = createAction((id: string) => ({
  type: [
    GET_PROJECT_STATUS_SUMMARY_REQUEST,
    GET_PROJECT_STATUS_SUMMARY_SUCCESS,
    GET_PROJECT_STATUS_SUMMARY_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.getStatusSummary(id),
}))

// Shares the summary slice with the read but drives `isSaving`, so the current
// summary stays on screen while Arc writes the new one.
export const [
  REFRESH_PROJECT_STATUS_SUMMARY_REQUEST,
  REFRESH_PROJECT_STATUS_SUMMARY_SUCCESS,
  REFRESH_PROJECT_STATUS_SUMMARY_FAILURE,
] = createRequestTypes(prefix, 'REFRESH_PROJECT_STATUS_SUMMARY')

export const refreshProjectStatusSummary = createAction((id: string) => ({
  type: [
    REFRESH_PROJECT_STATUS_SUMMARY_REQUEST,
    REFRESH_PROJECT_STATUS_SUMMARY_SUCCESS,
    REFRESH_PROJECT_STATUS_SUMMARY_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.refreshStatusSummary(id),
}))

// A member's hand-written update; also a saving action on the summary slice.
export const [
  SAVE_PROJECT_STATUS_UPDATE_REQUEST,
  SAVE_PROJECT_STATUS_UPDATE_SUCCESS,
  SAVE_PROJECT_STATUS_UPDATE_FAILURE,
] = createRequestTypes(prefix, 'SAVE_PROJECT_STATUS_UPDATE')

export const saveProjectStatusUpdate = createAction((id: string, data: object) => ({
  type: [
    SAVE_PROJECT_STATUS_UPDATE_REQUEST,
    SAVE_PROJECT_STATUS_UPDATE_SUCCESS,
    SAVE_PROJECT_STATUS_UPDATE_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.saveStatusUpdate(id, data),
}))

// The board's "Mark all read": takes the list's own view params; the reply carries the new badge count.
export const [
  MARK_ALL_PROJECT_TASKS_READ_REQUEST,
  MARK_ALL_PROJECT_TASKS_READ_SUCCESS,
  MARK_ALL_PROJECT_TASKS_READ_FAILURE,
] = createRequestTypes(prefix, 'MARK_ALL_PROJECT_TASKS_READ')

export const markAllProjectTasksRead = createAction((id: string, viewParams: object) => ({
  type: [
    MARK_ALL_PROJECT_TASKS_READ_REQUEST,
    MARK_ALL_PROJECT_TASKS_READ_SUCCESS,
    MARK_ALL_PROJECT_TASKS_READ_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.markAllTasksRead(id, viewParams),
  params: { projectId: id },
}))

// The calendar view's two reads of the list endpoint: tasks due in the visible weeks, and
// the undated ones listed beside the grid. Kept off `projectTasks` so the List view's paging stays its own.
export const [
  LIST_PROJECT_CALENDAR_TASKS_REQUEST,
  LIST_PROJECT_CALENDAR_TASKS_SUCCESS,
  LIST_PROJECT_CALENDAR_TASKS_FAILURE,
] = createRequestTypes(prefix, 'LIST_PROJECT_CALENDAR_TASKS')

export const listProjectCalendarTasks = createAction((id: string, params: object = {}) => ({
  type: [
    LIST_PROJECT_CALENDAR_TASKS_REQUEST,
    LIST_PROJECT_CALENDAR_TASKS_SUCCESS,
    LIST_PROJECT_CALENDAR_TASKS_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.listTasks(id, params),
  // The project rides along so a reducer can tell one board's pages from another's.
  params: { ...params, projectId: id },
}))

export const [
  LIST_PROJECT_UNDATED_TASKS_REQUEST,
  LIST_PROJECT_UNDATED_TASKS_SUCCESS,
  LIST_PROJECT_UNDATED_TASKS_FAILURE,
] = createRequestTypes(prefix, 'LIST_PROJECT_UNDATED_TASKS')

export const listProjectUndatedTasks = createAction((id: string, params: object = {}) => ({
  type: [
    LIST_PROJECT_UNDATED_TASKS_REQUEST,
    LIST_PROJECT_UNDATED_TASKS_SUCCESS,
    LIST_PROJECT_UNDATED_TASKS_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.listTasks(id, { ...params, hasDueDate: false }),
  // The project rides along so a reducer can tell one board's pages from another's.
  params: { ...params, projectId: id },
}))

// Moves a task between calendar days (or to/from "No due date") before its update returns.
export const MOVE_CALENDAR_TASK = `${prefix}/MOVE_CALENDAR_TASK`

export type MoveCalendarTaskAction = {
  type: typeof MOVE_CALENDAR_TASK
  task: ITaskProjectTask
  dueDate: string | null
}

export const moveCalendarTask = (
  task: ITaskProjectTask,
  dueDate: string | null,
): MoveCalendarTaskAction => ({ type: MOVE_CALENDAR_TASK, task, dueDate })

export const [SET_PROJECT_VIEW_REQUEST, SET_PROJECT_VIEW_SUCCESS, SET_PROJECT_VIEW_FAILURE] =
  createRequestTypes(prefix, 'SET_PROJECT_VIEW')

export const setProjectView = createAction((id: string, view: TaskProjectView) => ({
  type: [SET_PROJECT_VIEW_REQUEST, SET_PROJECT_VIEW_SUCCESS, SET_PROJECT_VIEW_FAILURE],
  promise: (api: IApi) => api.taskProjects.setView(id, { view }),
}))

export const [ADD_PROJECT_MEMBER_REQUEST, ADD_PROJECT_MEMBER_SUCCESS, ADD_PROJECT_MEMBER_FAILURE] =
  createRequestTypes(prefix, 'ADD_PROJECT_MEMBER')

export const addProjectMember = createAction((id: string, data: object) => ({
  type: [ADD_PROJECT_MEMBER_REQUEST, ADD_PROJECT_MEMBER_SUCCESS, ADD_PROJECT_MEMBER_FAILURE],
  promise: (api: IApi) => api.taskProjects.addMember(id, data),
}))

export const [UPDATE_MEMBER_ROLE_REQUEST, UPDATE_MEMBER_ROLE_SUCCESS, UPDATE_MEMBER_ROLE_FAILURE] =
  createRequestTypes(prefix, 'UPDATE_MEMBER_ROLE')

export const updateMemberRole = createAction((id: string, userId: string, data: object) => ({
  type: [UPDATE_MEMBER_ROLE_REQUEST, UPDATE_MEMBER_ROLE_SUCCESS, UPDATE_MEMBER_ROLE_FAILURE],
  promise: (api: IApi) => api.taskProjects.updateMemberRole(id, userId, data),
}))

export const [
  REMOVE_PROJECT_MEMBER_REQUEST,
  REMOVE_PROJECT_MEMBER_SUCCESS,
  REMOVE_PROJECT_MEMBER_FAILURE,
] = createRequestTypes(prefix, 'REMOVE_PROJECT_MEMBER')

export const removeProjectMember = createAction((id: string, userId: string) => ({
  type: [
    REMOVE_PROJECT_MEMBER_REQUEST,
    REMOVE_PROJECT_MEMBER_SUCCESS,
    REMOVE_PROJECT_MEMBER_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.removeMember(id, userId),
}))

export const [CREATE_SECTION_REQUEST, CREATE_SECTION_SUCCESS, CREATE_SECTION_FAILURE] =
  createRequestTypes(prefix, 'CREATE_SECTION')

export const createSection = createAction((id: string, data: object) => ({
  type: [CREATE_SECTION_REQUEST, CREATE_SECTION_SUCCESS, CREATE_SECTION_FAILURE],
  promise: (api: IApi) => api.taskProjects.createSection(id, data),
}))

export const [REORDER_SECTIONS_REQUEST, REORDER_SECTIONS_SUCCESS, REORDER_SECTIONS_FAILURE] =
  createRequestTypes(prefix, 'REORDER_SECTIONS')

export const reorderSections = createAction((id: string, sectionIds: string[]) => ({
  type: [REORDER_SECTIONS_REQUEST, REORDER_SECTIONS_SUCCESS, REORDER_SECTIONS_FAILURE],
  // Carried on the REQUEST so the reducer can reorder before the write lands.
  params: { projectId: id, sectionIds },
  promise: (api: IApi) => api.taskProjects.reorderSections(id, { sectionIds }),
}))

export const [UPDATE_SECTION_REQUEST, UPDATE_SECTION_SUCCESS, UPDATE_SECTION_FAILURE] =
  createRequestTypes(prefix, 'UPDATE_SECTION')

export const updateSection = createAction((id: string, sectionId: string, data: object) => ({
  type: [UPDATE_SECTION_REQUEST, UPDATE_SECTION_SUCCESS, UPDATE_SECTION_FAILURE],
  promise: (api: IApi) => api.taskProjects.updateSection(id, sectionId, data),
}))

export const [DELETE_SECTION_REQUEST, DELETE_SECTION_SUCCESS, DELETE_SECTION_FAILURE] =
  createRequestTypes(prefix, 'DELETE_SECTION')

export const deleteSection = createAction((id: string, sectionId: string) => ({
  type: [DELETE_SECTION_REQUEST, DELETE_SECTION_SUCCESS, DELETE_SECTION_FAILURE],
  promise: (api: IApi) => api.taskProjects.deleteSection(id, sectionId),
}))

export const [
  ADD_TASK_TO_PROJECT_REQUEST,
  ADD_TASK_TO_PROJECT_SUCCESS,
  ADD_TASK_TO_PROJECT_FAILURE,
] = createRequestTypes(prefix, 'ADD_TASK_TO_PROJECT')

export const addTaskToProject = createAction((id: string, data: object) => ({
  type: [ADD_TASK_TO_PROJECT_REQUEST, ADD_TASK_TO_PROJECT_SUCCESS, ADD_TASK_TO_PROJECT_FAILURE],
  promise: (api: IApi) => api.taskProjects.addTask(id, data),
}))

export const [
  ADD_TASKS_TO_PROJECT_REQUEST,
  ADD_TASKS_TO_PROJECT_SUCCESS,
  ADD_TASKS_TO_PROJECT_FAILURE,
] = createRequestTypes(prefix, 'ADD_TASKS_TO_PROJECT')

export const addTasksToProject = createAction(
  (id: string, data: { taskIds: string[]; sectionId?: string }) => ({
    type: [
      ADD_TASKS_TO_PROJECT_REQUEST,
      ADD_TASKS_TO_PROJECT_SUCCESS,
      ADD_TASKS_TO_PROJECT_FAILURE,
    ],
    promise: (api: IApi) => api.taskProjects.addTasks(id, data),
  }),
)

export const [
  MOVE_TASK_PLACEMENT_REQUEST,
  MOVE_TASK_PLACEMENT_SUCCESS,
  MOVE_TASK_PLACEMENT_FAILURE,
] = createRequestTypes(prefix, 'MOVE_TASK_PLACEMENT')

export const moveTaskPlacement = createAction(
  (
    id: string,
    taskId: string,
    data: { sectionId: string; position?: number },
    // Client-only: whether the board is in manual order, so the reducer knows to re-sort.
    isManualOrder = false,
    // Client-only: the moves to show when this request starts (default: just this one), so a
    // batch sent one write at a time lands on screen all at once.
    preview?: IPlacementMove[],
  ) => ({
    type: [MOVE_TASK_PLACEMENT_REQUEST, MOVE_TASK_PLACEMENT_SUCCESS, MOVE_TASK_PLACEMENT_FAILURE],
    params: { projectId: id, taskId, ...data, isManualOrder, preview },
    promise: (api: IApi) => api.taskProjects.moveTask(id, taskId, data),
  }),
)

export const [
  REMOVE_TASK_FROM_PROJECT_REQUEST,
  REMOVE_TASK_FROM_PROJECT_SUCCESS,
  REMOVE_TASK_FROM_PROJECT_FAILURE,
] = createRequestTypes(prefix, 'REMOVE_TASK_FROM_PROJECT')

export const removeTaskFromProject = createAction((id: string, taskId: string) => ({
  type: [
    REMOVE_TASK_FROM_PROJECT_REQUEST,
    REMOVE_TASK_FROM_PROJECT_SUCCESS,
    REMOVE_TASK_FROM_PROJECT_FAILURE,
  ],
  promise: (api: IApi) => api.taskProjects.removeTask(id, taskId),
}))

// Live updates for the open project page. Plain actions: webSocketMiddleware owns
// the subscription, so it can re-send it on every reconnect and dispatch the
// resync that closes the gap a dropped socket leaves.
export const SUBSCRIBE_PROJECT_UPDATES = `${prefix}/SUBSCRIBE_PROJECT_UPDATES`
export const UNSUBSCRIBE_PROJECT_UPDATES = `${prefix}/UNSUBSCRIBE_PROJECT_UPDATES`
export const PROJECT_UPDATES_RESYNC = `${prefix}/PROJECT_UPDATES_RESYNC`

// A type alias, not an interface, so it satisfies redux's index-signatured UnknownAction.
export type ProjectUpdatesAction = {
  type: string
  projectId: string
}

export const subscribeToProjectUpdates = (projectId: string): ProjectUpdatesAction => ({
  type: SUBSCRIBE_PROJECT_UPDATES,
  projectId,
})

export const unsubscribeFromProjectUpdates = (projectId: string): ProjectUpdatesAction => ({
  type: UNSUBSCRIBE_PROJECT_UPDATES,
  projectId,
})

export const resyncProjectUpdates = (projectId: string): ProjectUpdatesAction => ({
  type: PROJECT_UPDATES_RESYNC,
  projectId,
})
